import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import DashboardCard from './DashboardCard';
import { STATUS, formatTry } from '../constants';
import { Footnote } from '../styles';

const GaugeWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 10px;
`;

const GaugeCenter = styled.div`
  margin-top: -52px;
  text-align: center;
`;

const GaugePct = styled.div`
  font-size: 26px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const GaugeSub = styled.div`
  font-size: 11px;
  color: #6B7280;
`;

const StatLine = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #52514e;
  padding: 4px 0;

  strong { color: ${({ theme }) => theme.colors.text.primary}; }
`;

const MiniBars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 5px;
  height: 46px;
  margin-top: 8px;
`;

const MiniBar = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;

  > div {
    width: 100%;
    border-radius: 3px 3px 0 0;
    background: ${({ $active }) => ($active ? STATUS.normal : '#B7D3F6')};
    height: ${({ $h }) => $h}%;
    min-height: 2px;
  }

  > span {
    font-size: 8px;
    color: #9CA3AF;
  }
`;

export default function MonthPaceCard({ slice }) {
  const { t, i18n } = useTranslation();
  const s = slice.data;

  const model = useMemo(() => {
    if (!s) return null;
    const elapsedPct = Math.min(100, Math.round((s.elapsedDays / s.daysInMonth) * 100));
    const dailyAvg = s.elapsedDays > 0 ? s.approvedTotal / s.elapsedDays : 0;
    const forecast = dailyAvg * s.daysInMonth;
    const byDay = new Map(s.dailySeries.map((d) => [d.day, d.amount]));
    const last7 = [];
    for (let d = Math.max(1, s.elapsedDays - 6); d <= s.elapsedDays; d += 1) {
      last7.push({ day: d, amount: byDay.get(d) || 0 });
    }
    const maxLast7 = Math.max(1, ...last7.map((x) => x.amount));
    return { elapsedPct, dailyAvg, forecast, last7, maxLast7 };
  }, [s]);

  const monthName = s
    ? new Date(s.year, s.month - 1, 1).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })
    : '';

  // Yarım daire gauge (SVG)
  const angle = model ? (model.elapsedPct / 100) * 180 : 0;
  const rad = (deg) => ((deg - 180) * Math.PI) / 180;
  const arc = (deg) => `${90 + 70 * Math.cos(rad(deg))},${86 + 70 * Math.sin(rad(deg))}`;

  return (
    <DashboardCard
      cardKey="aylik-harcama-temposu"
      title={t('expenseDashboard.pace.title')}
      meta={monthName}
      span={4}
      spanMd={6}
      loading={slice.loading}
      empty={!s}
    >
      {model && (
        <div>
          <GaugeWrap>
            <svg width="180" height="96" viewBox="0 0 180 96" aria-hidden="true">
              <path d={`M ${arc(0)} A 70 70 0 0 1 ${arc(180)}`} fill="none" stroke="#EEF1F5" strokeWidth="12" strokeLinecap="round" />
              {model.elapsedPct > 0 && (
                <path d={`M ${arc(0)} A 70 70 0 0 1 ${arc(angle)}`} fill="none" stroke={STATUS.normal} strokeWidth="12" strokeLinecap="round" />
              )}
            </svg>
            <GaugeCenter>
              <GaugePct>%{model.elapsedPct}</GaugePct>
              <GaugeSub>{t('expenseDashboard.pace.dayOf', { day: s.elapsedDays, total: s.daysInMonth })}</GaugeSub>
            </GaugeCenter>
          </GaugeWrap>
          <StatLine>
            <span>{t('expenseDashboard.pace.dailyAvg')}</span>
            <strong>{formatTry(model.dailyAvg)}</strong>
          </StatLine>
          <StatLine>
            <span>{t('expenseDashboard.pace.forecast')}</span>
            <strong>{formatTry(model.forecast)}</strong>
          </StatLine>
          <Footnote>{t('expenseDashboard.pace.last7')}</Footnote>
          <MiniBars>
            {model.last7.map((d, i) => (
              <MiniBar key={d.day} $h={Math.round((d.amount / model.maxLast7) * 100)} $active={i === model.last7.length - 1}>
                <div title={`${d.day} — ${formatTry(d.amount)}`} />
                <span>{d.day}</span>
              </MiniBar>
            ))}
          </MiniBars>
        </div>
      )}
    </DashboardCard>
  );
}
